/**
 * Household Context
 * Feature 008: Household Profile Management
 *
 * Provides household-wide state management including:
 * - Household profile data
 * - Budget settings
 * - Family preferences
 * - Member management
 *
 * Dual-write pattern: loads from API first with localStorage fallback.
 * Saves to both API and localStorage.
 */

import React, { createContext, useCallback, useContext, useMemo, useRef, useState, useEffect, ReactNode } from 'react';
import type { HouseholdProfile, BudgetSettings, FamilyPreferences } from '../types';
import {
  getHouseholdProfile,
  saveHouseholdProfile,
  initializeHousehold,
  updateBudget as updateBudgetService,
  updatePreferences as updatePreferencesService,
  getMemberProfiles,
} from '../services/householdService';
import { api } from '../services/apiClient';
import { logger } from '../utils/logger';
import { isApiEnabled, FEATURE_FLAGS } from '../utils/featureFlags';

// --- API response types ---

interface HouseholdApiData {
  id: string;
  userId: string;
  name: string;
  budgetAmount: string | null;
  budgetPeriod: string | null;
  budgetStrict: boolean;
  dietaryFocus: string | null;
  neverBuyList: string[];
  preferredMealTypes: string[];
  members: Array<{
    id: string;
    name: string;
    avatar: string | null;
    ageRange: string | null;
    isActive: boolean;
  }>;
}

interface HouseholdApiResponse {
  data: HouseholdApiData | null;
}

// --- API helpers ---

/** Try to load household from API. Returns null on failure. */
async function fetchHouseholdFromApi(): Promise<HouseholdProfile | null> {
  if (!isApiEnabled(FEATURE_FLAGS.household)) return null;
  try {
    const res = await api.get<HouseholdApiResponse>('/household');
    if (!res.data) return null;

    const d = res.data;
    // Map API data to HouseholdProfile shape
    return {
      id: d.id,
      name: d.name,
      memberProfileIds: d.members.map(m => m.id),
      budget: d.budgetAmount ? {
        amount: parseFloat(d.budgetAmount),
        period: (d.budgetPeriod as BudgetSettings['period']) || 'weekly',
        strictMode: d.budgetStrict,
      } : undefined,
      preferences: {
        neverBuy: d.neverBuyList || [],
        preferredMealTypes: d.preferredMealTypes || [],
        dietaryFocus: d.dietaryFocus || undefined,
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  } catch (err) {
    logger.error('Failed to load household from API:', err);
    return null;
  }
}

/** Push household settings to API (best-effort, non-blocking). */
function syncHouseholdToApi(profile: HouseholdProfile): void {
  if (!isApiEnabled(FEATURE_FLAGS.household)) return;
  api.patch('/household', {
    name: profile.name,
    budgetAmount: profile.budget?.amount?.toString() ?? null,
    budgetPeriod: profile.budget?.period ?? null,
    budgetStrict: profile.budget?.strictMode ?? false,
    dietaryFocus: profile.preferences?.dietaryFocus ?? null,
    neverBuyList: profile.preferences?.neverBuy ?? [],
    preferredMealTypes: profile.preferences?.preferredMealTypes ?? [],
  }).catch((err) => {
    logger.error('Failed to sync household to API:', err);
  });
}

/** Push budget update to API (best-effort, non-blocking). */
function syncBudgetToApi(budget: BudgetSettings): void {
  if (!isApiEnabled(FEATURE_FLAGS.household)) return;
  api.patch('/household', {
    budgetAmount: budget.amount?.toString() ?? null,
    budgetPeriod: budget.period ?? null,
    budgetStrict: budget.strictMode ?? false,
  }).catch((err) => {
    logger.error('Failed to sync budget to API:', err);
  });
}

/** Push preferences update to API (best-effort, non-blocking). */
function syncPreferencesToApi(preferences: FamilyPreferences): void {
  if (!isApiEnabled(FEATURE_FLAGS.household)) return;
  api.patch('/household', {
    dietaryFocus: preferences.dietaryFocus ?? null,
    neverBuyList: preferences.neverBuy ?? [],
    preferredMealTypes: preferences.preferredMealTypes ?? [],
  }).catch((err) => {
    logger.error('Failed to sync preferences to API:', err);
  });
}

interface HouseholdContextType {
  householdProfile: HouseholdProfile | null;
  setHouseholdProfile: (profile: HouseholdProfile) => void;
  updateBudget: (budget: BudgetSettings) => void;
  updatePreferences: (preferences: FamilyPreferences) => void;
  updateHouseholdName: (name: string) => void;
  refreshHousehold: () => void;
  isLoading: boolean;
  apiError: string | null;
}

const HouseholdContext = createContext<HouseholdContextType | undefined>(undefined);

interface HouseholdProviderProps {
  children: ReactNode;
}

export function HouseholdProvider({ children }: HouseholdProviderProps) {
  const [householdProfile, setHouseholdProfileState] = useState<HouseholdProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);

  // Load household profile: API first, localStorage fallback
  useEffect(() => {
    let cancelled = false;

    const loadHousehold = async () => {
      setApiError(null);

      try {
        // Try API first
        const apiProfile = await fetchHouseholdFromApi();
        if (!cancelled && apiProfile) {
          setHouseholdProfileState(apiProfile);
          // Update localStorage cache
          saveHouseholdProfile(apiProfile);
          setIsLoading(false);
          return;
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'API load failed';
          setApiError(message);
          logger.error('API household load failed, falling back to localStorage:', err);
        }
      }

      // Fall back to localStorage
      if (!cancelled) {
        try {
          const profile = getHouseholdProfile();

          // If no household exists, initialize with empty member list
          if (!profile) {
            // Get existing user profiles to populate household
            const profilesJson = localStorage.getItem('userProfiles');
            const memberIds: string[] = [];

            if (profilesJson) {
              try {
                const profiles = JSON.parse(profilesJson);
                if (Array.isArray(profiles)) {
                  // Add all existing profile IDs
                  memberIds.push(...profiles.map((p: { id: string }) => p.id));
                }
              } catch (e) {
                logger.error('Error parsing user profiles:', e);
              }
            }

            const newHousehold = initializeHousehold(memberIds);
            setHouseholdProfileState(newHousehold);
          } else {
            setHouseholdProfileState(profile);
          }
        } catch (error) {
          logger.error('Error loading household:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadHousehold();

    return () => { cancelled = true; };
  }, []);

  // Debounced save for household profile (300ms) - saves to both localStorage and API
  const householdSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncedSaveHousehold = useCallback((profile: HouseholdProfile) => {
    if (householdSaveTimer.current) clearTimeout(householdSaveTimer.current);
    householdSaveTimer.current = setTimeout(() => {
      // Save to localStorage
      saveHouseholdProfile(profile);
      // Dual-write: also push to API (non-blocking)
      syncHouseholdToApi(profile);
    }, 300);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (householdSaveTimer.current) clearTimeout(householdSaveTimer.current);
    };
  }, []);

  const setHouseholdProfile = useCallback((profile: HouseholdProfile) => {
    debouncedSaveHousehold(profile);
    setHouseholdProfileState(profile);
  }, [debouncedSaveHousehold]);

  const updateBudget = useCallback((budget: BudgetSettings) => {
    try {
      // Save to localStorage via service
      updateBudgetService(budget);
      const updatedProfile = getHouseholdProfile();
      if (updatedProfile) {
        setHouseholdProfileState(updatedProfile);
      }
      // Dual-write: also push to API (non-blocking)
      syncBudgetToApi(budget);
    } catch (error) {
      logger.error('Error updating budget:', error);
      throw error;
    }
  }, []);

  const updatePreferences = useCallback((preferences: FamilyPreferences) => {
    try {
      // Save to localStorage via service
      updatePreferencesService(preferences);
      const updatedProfile = getHouseholdProfile();
      if (updatedProfile) {
        setHouseholdProfileState(updatedProfile);
      }
      // Dual-write: also push to API (non-blocking)
      syncPreferencesToApi(preferences);
    } catch (error) {
      logger.error('Error updating preferences:', error);
      throw error;
    }
  }, []);

  const updateHouseholdName = useCallback((name: string) => {
    setHouseholdProfileState(prev => {
      if (!prev) return prev;
      const updated = { ...prev, name };
      debouncedSaveHousehold(updated);
      return updated;
    });
  }, [debouncedSaveHousehold]);

  const refreshHousehold = useCallback(async () => {
    // Try API first on refresh
    try {
      const apiProfile = await fetchHouseholdFromApi();
      if (apiProfile) {
        setHouseholdProfileState(apiProfile);
        saveHouseholdProfile(apiProfile);
        return;
      }
    } catch {
      // Fall through to localStorage
    }

    const profile = getHouseholdProfile();
    setHouseholdProfileState(profile);
  }, []);

  const value: HouseholdContextType = useMemo(() => ({
    householdProfile,
    setHouseholdProfile,
    updateBudget,
    updatePreferences,
    updateHouseholdName,
    refreshHousehold,
    isLoading,
    apiError,
  }), [householdProfile, setHouseholdProfile, updateBudget, updatePreferences, updateHouseholdName, refreshHousehold, isLoading, apiError]);

  return (
    <HouseholdContext.Provider value={value}>
      {children}
    </HouseholdContext.Provider>
  );
}

export function useHousehold() {
  const context = useContext(HouseholdContext);
  if (context === undefined) {
    throw new Error('useHousehold must be used within a HouseholdProvider');
  }
  return context;
}
